import IdleJs from "idle-js";

type IdleSubscriber = () => void | Promise<void>;

type SubscriberOptions = {
  /** Not tied to the signed-in user (e.g. the app version check). */
  keepAcrossSessions?: boolean;
};

/*
 * The client activity monitoring manager:
 * If the client puts the computer to sleep or hibernation and then returns,
 * we need the store to automatically update all its states,
 * as the client was absent for an unknown period and changes might have occurred in the show data.
 * */
export class IdleManager {
  private static instance: IdleManager;

  private idle: IdleJs;

  private subscribers: Map<string, IdleSubscriber> = new Map();

  private sessionlessKeys: Set<string> = new Set();

  private constructor() {
    this.idle = new IdleJs({
      idle: 60_000,
      onActive: async () => {
        this.subscribers.forEach((subscriber: IdleSubscriber) => {
          subscriber();
        });
      },
    });
    this.idle.start();
  }

  public static getInstance(): IdleManager {
    if (!IdleManager.instance) {
      IdleManager.instance = new IdleManager();
    }
    return IdleManager.instance;
  }

  public addSubscriber(
    key: string,
    callback: IdleSubscriber,
    { keepAcrossSessions = false }: SubscriberOptions = {}
  ): void {
    if (!this.subscribers.has(key)) {
      this.subscribers.set(key, callback);
      if (keepAcrossSessions) this.sessionlessKeys.add(key);
    }
  }

  public removeSubscriber(key: string): void {
    this.subscribers.delete(key);
    this.sessionlessKeys.delete(key);
  }

  /**
   * Session subscribers close over the identity that registered them. Logging
   * out or switching to an impersonated user happens without a page reload, so
   * the previous user's refreshes must go with it — otherwise the next
   * `onActive` calls e.g. organiser endpoints with the exhibitor's token (403s).
   */
  public clearSessionSubscribers(): void {
    this.subscribers.forEach((_, key) => {
      if (!this.sessionlessKeys.has(key)) this.subscribers.delete(key);
    });
  }
}
